import { useEffect, useState, type ReactNode } from 'react';
import { httpsCallable } from 'firebase/functions';
import { DISCLAIMERS, coachingOutputSchema } from '@erectus/shared';
import type { CoachingInput, CoachingOutput, Exercise, SessionSummary } from '@erectus/shared';
import { Button, Card, Spinner } from '@/ui/primitives.js';
import { firebase } from '@/lib/firebase.js';
import { getRecommendationForSession, rateRecommendation } from '@/lib/repo.js';
import { latencyBucket, count, track } from '@/lib/analytics.js';
import { toCoachingDto } from '@/scoring/sessionScore.js';
import type { UserDoc } from '@erectus/shared';
import { TAG_LABEL } from '@/features/exercises/tags.js';
import { Icon } from '@/ui/icons.js';

/**
 * Coaching card (docs/PRODUCT.md §4).
 *
 * The callable does the LLM work; the client only shows it. If generation fails
 * the report still stands on its own — coaching is a bonus, not the product.
 */
interface CoachingState {
  status: 'idle' | 'loading' | 'ready' | 'failed';
  output: CoachingOutput | null;
  recId: string | null;
}

export function CoachingCard({
  uid,
  sessionId,
  summary,
  userDoc,
  exercises,
}: {
  uid: string;
  sessionId: string;
  summary: SessionSummary;
  userDoc: UserDoc | null;
  exercises: Exercise[];
}): ReactNode {
  const [state, setState] = useState<CoachingState>({ status: 'idle', output: null, recId: null });
  const [rating, setRating] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      setState({ status: 'loading', output: null, recId: null });
      // Already generated (e.g. the user reopened the report)?
      const existing = await getRecommendationForSession(uid, sessionId).catch(() => null);
      if (cancelled) return;
      if (existing) {
        setState({
          status: 'ready',
          output: {
            coachingText: existing.coachingText,
            exerciseIds: existing.exerciseIds,
            rationale: existing.rationale,
            safetyFlags: existing.safetyFlags,
          },
          recId: existing.id,
        });
        setRating(existing.userRating ?? null);
        return;
      }

      const startedAt = Date.now();
      try {
        const callable = httpsCallable<CoachingInput, CoachingOutput & { recId: string; provider: 'gemini' | 'claude' }>(
          firebase().functions,
          'generateCoaching',
        );
        const payload: CoachingInput = {
          sessionId,
          sessionSummary: toCoachingDto({
            ...summary,
            cva: summary.cva ?? null,
            cha: summary.cha ?? null,
            recoveryRate: 0,
            stateSeconds: { good: 0, forwardHead: 0, slouch: 0, recline: 0, invalid: 0 },
          }),
          user: {
            goals: userDoc?.profile?.goals ?? [],
            equipment:
              userDoc?.profile?.equipment ?? { laptop: true, monitor: false, chairType: 'office' },
            sittingHoursBucket: userDoc?.profile?.sittingHoursBucket ?? '6-8',
          },
          locale: 'en',
        };
        const response = await callable(payload);
        if (cancelled) return;
        const parsed = coachingOutputSchema.parse(response.data);
        setState({ status: 'ready', output: parsed, recId: response.data.recId ?? null });
        track('coaching_generated', {
          provider: response.data.provider ?? 'gemini',
          latency_bucket: latencyBucket(Date.now() - startedAt),
          safety_flag_count: count(parsed.safetyFlags.length),
        });
      } catch {
        if (!cancelled) setState({ status: 'failed', output: null, recId: null });
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [sessionId, summary, uid, userDoc]);

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <Card className="space-y-3">
        <Spinner label="Writing your coaching note" />
        <div className="h-3 w-4/5 animate-pulse rounded bg-line" />
        <div className="h-3 w-3/5 animate-pulse rounded bg-line" />
      </Card>
    );
  }

  if (state.status === 'failed' || !state.output) {
    return (
      <Card>
        <h2 className="font-semibold">Coaching note unavailable</h2>
        <p className="mt-1 text-sm text-muted">
          We could not write one this time. Your session numbers above are unaffected.
        </p>
      </Card>
    );
  }

  const picked = state.output.exerciseIds
    .map((id) => exercises.find((e) => e.id === id))
    .filter((e): e is Exercise => Boolean(e));

  const rate = async (value: number): Promise<void> => {
    setRating(value);
    if (!state.recId) return;
    await rateRecommendation(state.recId, value).catch(() => null);
    track('coaching_rated', { rating: value as 1 | 2 | 3 | 4 | 5 });
  };

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="font-semibold">Coaching note</h2>
        <p className="mt-1 text-sm leading-relaxed text-ink-soft">{state.output.coachingText}</p>
        <p className="mt-2 text-xs text-muted">{state.output.rationale}</p>
      </div>

      {picked.length > 0 ? (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold">Try these</h3>
          {picked.map((exercise) => (
            <div key={exercise.id} className="rounded-xl border border-line px-3 py-2">
              <p className="text-sm font-medium">{exercise.name}</p>
              <p className="text-xs text-muted">
                {exercise.reps} · {exercise.durationSec}s · {exercise.tags.map((t) => TAG_LABEL[t] ?? t).join(', ')}
              </p>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted">Useful?</span>
        {[1, 2, 3, 4, 5].map((value) => (
          <button
            key={value}
            aria-label={`Rate ${value} out of 5`}
            onClick={() => void rate(value)}
            className={rating !== null && value <= rating ? 'text-ink' : 'text-ink/25'}
          >
            <Icon
              name="star"
              size={22}
              fill={rating !== null && value <= rating ? 'currentColor' : 'none'}
            />
          </button>
        ))}
      </div>

      <p className="text-xs text-muted">{DISCLAIMERS.coachingFooter}</p>
      {flagNotes(state.output.safetyFlags).map((note) => (
        <p key={note} className="text-[11px] text-muted">
          {note}
        </p>
      ))}
    </Card>
  );
}

export function CoachingActions({ onRetry }: { onRetry(): void }): ReactNode {
  return (
    <Button variant="secondary" onClick={onRetry}>
      Try again
    </Button>
  );
}

/**
 * The coach marks what it held back (safetyFlags, an internal tag list). Show
 * the reader what that means in a sentence; tags without one stay internal.
 */
const FLAG_NOTES: Record<string, string> = {
  'low-valid-ratio': 'Only part of this session could be measured, so the advice stays general.',
  'insufficient-data': 'Too little was recorded for specific advice.',
  'extreme-angle': 'Some readings looked implausible and were left out.',
};

function flagNotes(flags: readonly string[]): string[] {
  return [...new Set(flags.map((f) => FLAG_NOTES[f]).filter((n): n is string => Boolean(n)))];
}
